/* ===========================================================================
   Umwelt & Verantwortung – Mengenrechner
   ---------------------------------------------------------------------------
   Macht die serverseitig gerenderte Rechnerkarte interaktiv. Es wird kein
   Markup erzeugt, nur Text in vorhandenen [data-calc-out]-Feldern ersetzt.
   Formeln und Zahlenformat entsprechen sections/umwelt-rechner.liquid und
   snippets/formatted-number.liquid – Aenderungen immer an beiden Stellen.

   Umweltwerte: CO₂e und Baumaequivalent werden nur gerechnet, wenn im
   Theme-Editor ein Faktor hinterlegt ist (data-co2 / data-baum > 0). Ohne
   Faktor gibt es die Felder im Markup gar nicht – dann bleibt hier nichts
   zu tun. Es wird niemals ein Ersatzwert erfunden.
   Kein Tracking, keine Speicherung, keine externen Abhaengigkeiten.
   =========================================================================== */

(function () {
  'use strict';

  var TAGE_PRO_JAHR = 365;

  /* Rundung wie Liquid: halbe Stellen von null weg. */
  function roundHalfAway(value) {
    return value < 0 ? -Math.round(-value) : Math.round(value);
  }

  /* Deutsches Zahlenformat – Gegenstueck zu snippets/formatted-number.liquid. */
  function formatNumber(value, decimals, trim) {
    decimals = decimals || 0;
    var factor = Math.pow(10, decimals);
    var scaled = roundHalfAway(value * factor);
    var sign = '';
    if (scaled < 0) {
      sign = '−';
      scaled = -scaled;
    }
    var integer = String(Math.floor(scaled / factor)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    var fraction = '';
    if (decimals > 0) {
      fraction = String(scaled % factor);
      while (fraction.length < decimals) fraction = '0' + fraction;
      if (trim) fraction = fraction.replace(/0+$/, '');
    }
    return sign + integer + (fraction ? ',' + fraction : '');
  }

  function plural(count, one, many) {
    return count === 1 ? one : many;
  }

  /* Reine Berechnung – ohne DOM. Gerechnet wird in Millilitern, damit
     Rundungen an halben Litern exakt sind und mit Liquid uebereinstimmen. */
  function calculate(v, packSize, co2Factor, treeFactor) {
    var persons = Math.round(v.persons);
    var years = Math.round(v.years);
    var milliliters = persons * Math.round(v.liters * 1000) * TAGE_PRO_JAHR * years;
    var bottleMl = Math.max(1, Math.round(v.bottle * 1000));
    var bottles = Math.ceil(milliliters / bottleMl);
    var result = {
      persons: persons,
      years: years,
      liters: milliliters / 1000,
      bottles: bottles,
      packs: Math.ceil(bottles / packSize),
      co2Kg: null,
      trees: null
    };
    if (co2Factor > 0) {
      /* Math.floor spiegelt die Ganzzahl-Division in Liquid. */
      result.co2Kg = (Math.floor(milliliters / 1000) * co2Factor) / 1000;
      if (treeFactor > 0 && years > 0) {
        result.trees = result.co2Kg / (treeFactor * years);
      }
    }
    return result;
  }

  function setup(root) {
    if (root.dataset.bound === 'true') return;
    root.dataset.bound = 'true';

    var packSize = parseInt(root.dataset.pack, 10) > 0 ? parseInt(root.dataset.pack, 10) : 6;
    var co2Factor = parseFloat(root.dataset.co2) > 0 ? parseFloat(root.dataset.co2) : 0;
    var treeFactor = parseFloat(root.dataset.baum) > 0 ? parseFloat(root.dataset.baum) : 0;

    var inputs = {};
    Array.prototype.forEach.call(root.querySelectorAll('[data-calc-input]'), function (input) {
      inputs[input.dataset.calcInput] = input;
    });
    if (!inputs.persons || !inputs.liters || !inputs.years || !inputs.bottle) return;

    var outputs = {};
    Array.prototype.forEach.call(root.querySelectorAll('[data-calc-out]'), function (node) {
      (outputs[node.dataset.calcOut] = outputs[node.dataset.calcOut] || []).push(node);
    });
    var live = root.querySelector('[data-calc-live]');
    var liveTimer = null;

    function set(key, text) {
      (outputs[key] || []).forEach(function (node) {
        if (node.textContent !== text) node.textContent = text;
      });
    }

    function read(name) {
      var value = parseFloat(inputs[name].value);
      return isFinite(value) ? value : 0;
    }

    function paintFill(input) {
      var min = parseFloat(input.min);
      var max = parseFloat(input.max);
      var value = parseFloat(input.value);
      var percent = max > min ? ((value - min) / (max - min)) * 100 : 0;
      input.style.setProperty('--wq-fill', Math.min(100, Math.max(0, percent)).toFixed(2) + '%');
    }

    function update(announce) {
      var v = {
        persons: read('persons'),
        liters: read('liters'),
        years: read('years'),
        bottle: read('bottle')
      };
      var r = calculate(v, packSize, co2Factor, treeFactor);

      var bottleText = formatNumber(v.bottle, 2, true) + ' Liter';
      var texts = {
        persons: formatNumber(r.persons, 0) + ' ' + plural(r.persons, 'Person', 'Personen'),
        liters: formatNumber(v.liters, 2, true) + ' Liter pro Tag',
        years: formatNumber(r.years, 0) + ' ' + plural(r.years, 'Jahr', 'Jahre'),
        bottle: bottleText
      };

      Object.keys(texts).forEach(function (name) {
        set(name, texts[name]);
        inputs[name].setAttribute('aria-valuetext', texts[name]);
        paintFill(inputs[name]);
      });

      var yearsIn = 'in ' + formatNumber(r.years, 0) + ' ' + plural(r.years, 'Jahr', 'Jahren');
      var litersText = formatNumber(r.liters, 0) + ' Liter';
      var bottlesText = formatNumber(r.bottles, 0) + ' ' + plural(r.bottles, 'Flasche', 'Flaschen');

      set('years_result', texts.years);
      set('years_in', yearsIn);
      set('basis', texts.persons + ' und ' + texts.liters);
      set('liters_total', litersText);
      set('bottles', bottlesText);
      set('bottles_caption', 'à ' + bottleText + ', rund ' + formatNumber(r.packs, 0) + ' Gebinde mit je ' + packSize + ' Flaschen');
      set('formula_liters', formatNumber(r.persons, 0) + ' × ' + formatNumber(v.liters, 2, true) + ' × 365 × ' + formatNumber(r.years, 0) + ' = ' + formatNumber(r.liters, 2, true) + ' Liter');
      set('formula_bottles', formatNumber(r.liters, 2, true) + ' ÷ ' + formatNumber(v.bottle, 2, true) + ' = ' + formatNumber(r.bottles, 0) + ' Flaschen');

      /* Nur mit belegtem Faktor – sonst existieren die Felder nicht. */
      if (r.co2Kg !== null) set('co2', formatNumber(r.co2Kg, 0) + ' kg CO₂e');
      if (r.trees !== null) {
        var treesText = formatNumber(r.trees, 1, true);
        set('trees', treesText + ' ' + (treesText === '1' ? 'Baum' : 'Bäume'));
      }

      if (announce && live) {
        window.clearTimeout(liveTimer);
        liveTimer = window.setTimeout(function () {
          live.textContent = 'Flaschenwasser ' + yearsIn + ': ' + litersText + ', ' + bottlesText + '.';
        }, 700);
      }
    }

    Object.keys(inputs).forEach(function (name) {
      inputs[name].addEventListener('input', function () {
        update(true);
      });
    });

    update(false);
  }

  function init(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-umwelt-rechner]'), function (root) {
      try {
        setup(root);
      } catch (error) {
        /* Rechner bleibt mit den serverseitigen Startwerten stehen. */
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
